// --- src/data/m3/Unit2.js ---

export const M3_UNIT_2 = {
  id: "u2-at-the-shops",
  title: "Unit 2: At the Shops",
  topic: "Shopping verbs + too/enough + fit + dialogues",
  iconType: "shopping-cart",
  grammarImage: "/m3-u2-shops.jpg",
  grammarText: "Unit focus: shopping verbs (buy, sell, try on, fit, pay, choose) + 'too' (problem) + 'not...enough' (insufficient) + real store dialogues + payment methods.",
  sets: [

    // ===================================================================
    // SET 1: SHOPPING VERBS BASICS (A1+)
    // ===================================================================
    {
      id: "m3-u2-set1",
      title: "Set 1: Welcome to the Mall",
      context: `*Sábado por la mañana - Centro Comercial Eaton*

Carlos y su hermana Ana llegan al centro comercial.
"Necesito comprar zapatos nuevos," dice Carlos. "Y yo quiero ver vestidos," responde Ana.
Entran a la primera tienda.

*Saturday morning - Eaton Shopping Mall*

Carlos and his sister Ana arrive at the shopping mall.
"I need to buy new shoes," says Carlos. "And I want to see dresses," responds Ana.
They enter the first store.`,
      
      questions: [
        {
          type: "match",
          q: "Match the shopping verb to its meaning:",
          pairs: [
            { left: "Buy", right: "Pay money for something" },
            { left: "Sell", right: "Give something for money" },
            { left: "Want", right: "Desire to have" }
          ],
          a: "completed",
          successTip: "SHOPPING BASICS! Buy=💵get, Sell=💰give, Want=💭desire! 🛍️"
        },
        {
          type: "mc",
          q: "Carlos says: 'I _____ new shoes for work.'",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "I → want (no 's')."
        },
        {
          type: "order",
          q: "Traduce: 'Quiero comprar zapatos.'",
          words: ["I", "want", "to", "buy", "shoes."],
          a: "I want to buy shoes.",
          extra: ["socks", "a dress", "a shirt"],
          tip: "Want + to + verb.",
          successTip: "Utiliza 'want to + verb' siempre que tengas 2 verbos. 🛍️ 🛒"
        },
        {
          type: "mc",
          q: "The shop _____ clothes and shoes.",
          options: ["sells", "sell", "sold"],
          a: "sells",
          successTip: "SHOP (singular) → SELLS! Third person +S! 🏪"
        },
        {
          type: "input",
          q: "Ana asks the clerk: 'Do you _____ dresses here?'",
          a: "sell",
          alts: ["have"],
          tip: "'Do you sell...?' = common question."
        },
        {
          type: "match",
          q: "Match the action to the place:",
          pairs: [
            { left: "Buy clothes", right: "Clothing store" },
            { left: "Buy food", right: "Supermarket" },
            { left: "Buy books", right: "Bookstore" }
          ],
          a: "completed",
          successTip: "Do not confuse library with bookstore! 📚🍎👕"
        },
        {
          type: "mc",
          q: "Ana _____ buy a red dress.",
          options: ["wants to", "want", "want to","wanting to"],
          a: "wants to",
          tip: "want + third person + to + verb."
        },
        {
          type: "input",
          q: "They _____ fruit at the market every week. ",
          a: "buy",
          alts: ["get", "purchase","sell"],
        },
        {
          type: "order",
          q: "Traduce: 'Ella vende ropa hermosa.'",
          words: ["She", "sells", "beautiful", "clothes."],
          a: "She sells beautiful clothes.",
          extra: ["shoes", "a dress", "a shirt"],
          tip: "She → sells (third person)."
        },
        {
          type: "mc",
          q: "Carlos asks Ana: 'Do you _____ these shoes?'",
          options: ["want", "wants", "wanting"],
          a: "want",
          tip: "WANT! I/you/we/they want (no S)! She/he/it wantS! 👟"
        }
      ]
    },

    // ===================================================================
    // SET 2: TRY ON / OWN / CHOOSE (A1+/A2)
    // ===================================================================
    {
      id: "m3-u2-set2",
      title: "Set 2: In the Store",
      context: `*Dentro de la tienda "Style & Co."*

Ana encuentra un vestido verde que le gusta.
"Puedo probarmelo?" le pregunta a la vendedora.
"Por supuesto, el probador esta allá" responde la vendedora.

*Inside "Style & Co." store*

Ana finds a green dress she likes.
"Can I try this on?" she asks the sales assistant.
"Of course! The fitting room is over there," responds the assistant.`,
      
      questions: [
        {
          type: "match",
          q: "Match the verb to its definition:",
          pairs: [
            { left: "Try on", right: "Put on to test" },
            { left: "Own", right: "Possess, have" },
            { left: "Choose", right: "Select one option" }
          ],
          a: "completed",
          successTip: "SHOPPING ACTIONS! Try on=🧥test, Own=✅have, Choose=👉pick!"
        },
        {
          type: "mc",
          q: "Ana says: 'I always _____ clothes before I buy them.'",
          options: ["try on", "sell", "own"],
          a: "try on",
          tip: "Try on = test clothing before buying."
        },
        {
          type: "order",
          q: "Traduce: '¿Puedo probarme esto?'",
          words: ["Can", "I", "try", "this", "on?"],
          a: "Can I try this on?",
          extra: ["put","fit"],
          successTip: "No olvides que try on es para ropa! 🧥👟"
        },
        {
          type: "input",
          q: "She _____ a beautiful green dress. (elegir)",
          a: "chooses",
          alts: ["choose"],
          successTip: "SHE/HE/IT → verb+ES! She CHOOSES! ✅"
        },
        {
          type: "match",
          q: "Match the statement to the action:",
          pairs: [
            { left: "I own 30 pairs of shoes", right: "I have many shoes" },
            { left: "She chooses red", right: "She selects red" },
            { left: "They try on jeans", right: "They test jeans" }
          ],
          a: "completed",
          successTip: "SYNONYMS! Own=have, Choose=select, Try on=test! 👠✨"
        },
        {
          type: "input",
          q: "Carlos _____ 10 expensive watches.",
          a: "owns",
          alts: ["own","have","has"],
          tip: "Own = possess, have."
        },
        {
          type: "mc",
          q: "The sales assistant advises: '_____ these shoes before buying!'",
          options: ["Try on", "Own", "Choose"],
          a: "Try on",
          successTip: "GOLDEN RULE! Always try on BEFORE buying! 🛍️✅"
        },
        {
          type: "order",
          q: "Traduce: 'Ella siempre elige ropa azul.'",
          words: ["She", "always", "chooses", "blue", "clothes."],
          a: "She always chooses blue clothes.",
          extra: ["night","evening"],
          tip: "Adverb (always) goes before main verb."
        },
        {
          type: "input",
          q: "We _____ _____ new clothes every season. (probar)",
          a: "try on",
          alts: ["try"],
          tip: "Try on = probar (ropa)."
        },
        {
          type: "mc",
          q: "Ana's mom always _____ her clothes for her!",
          options: ["chooses", "choose", "choosing"],
          a: "chooses",
          successTip: "MOM (she) → CHOOSES! Third person rule! 👩👗"
        }
      ]
    },

    // ===================================================================
    // SET 3: FIT / PAY (A2)
    // ===================================================================
    {
      id: "m3-u2-set3",
      title: "Set 3: Does It Fit?",
      context: `*Probador de ropa*

Ana sale del probador con el vestido verde puesto.
"¿Te queda bien?" pregunta Carlos. "Perfecto!" responde Ana.
Van juntos a la caja a pagar.

*Fitting room*

Ana comes out of the fitting room wearing the green dress.
"Does it fit?" asks Carlos. "Perfect!" Ana answers.
They go together to the checkout to pay.`,
      
      questions: [
        {
          type: "match",
          q: "Match the question to the answer:",
          pairs: [
            { left: "Does it fit?", right: "Yes, perfect!" },
            { left: "How do you pay?", right: "By credit card" },
            { left: "What size?", right: "Medium, please" }
          ],
          a: "completed",
          successTip: "REAL DIALOGUE! Fit→size, Pay→money! Common questions! 💳👗"
        },
        {
          type: "input",
          q: "Carlos tries on jeans: 'These jeans don't _____. They're too small.'",
          a: "fit",
          tip: "Fit = be the right size."
        },
        {
          type: "mc",
          q: "The shoes _____ me perfectly!",
          options: ["fit", "fits", "fitting"],
          a: "fit",
          successTip: "SHOES (plural) → FIT! No 'S'! 👟✅"
        },
        {
          type: "order",
          q: "Traduce: '¿Te queda bien este vestido?'",
          words: ["Does", "this", "dress", "fit", "you?"],
          a: "Does this dress fit you?",
          extra: ["look", "suit"],
          tip: "Common fitting room question!",
          successTip: "It fits you = Es de tu talla. It suits you = Se te ve bien"
        },
        {
          type: "match",
          q: "Match the payment method:",
          pairs: [
            { left: "Cash", right: "Bills and coins" },
            { left: "Contactless", right: "Not inserting the card" },
            { left: "Debit card", right: "Bank card" }
          ],
          a: "completed",
          successTip: "Now people can pay with a phone, watch, etc! 💵💳"
        },
        {
          type: "mc",
          q: "Ana says: 'I always _____ by credit card.'",
          options: ["pay", "pays", "paid"],
          a: "pay",
          tip: "I → pay (no 's')."
        },
        {
          type: "input",
          q: "The sales assistant says: 'This sweater _____ you. It's the right size.'",
          a: "fits",
          alts: ["fit"],
          tip: "Sweater (singular) → fits."
        },
        {
          type: "order",
          q: "Traduce: 'Pagamos en efectivo.'",
          words: ["We", "pay", "with", "cash."],
          a: "We pay with cash.",
          extra: ["card"],
          tip: "Pay WITH/IN cash / BY card.",
          successTip: "Pay with cash es mas neutral, pay in cash es mas coloquial pero pay cash es mas usada"
        },
        {
          type: "input",
          q: "They _____ for their shopping by card.",
          a: "pay",
          successTip: "PAY BY CARD! UK: 'contactless', US: 'tap to pay'! 🛒💳"
        },
        {
          type: "mc",
          q: "Carlos asks the clerk: '_____ this jacket fit my brother?'",
          options: ["Does", "Do", "Suits"],
          a: "Does",
          successTip: "JACKET (singular) → DOES! Does it fit? ✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: TOO + ADJECTIVE (A2)
    // ===================================================================
    {
      id: "m3-u2-set4",
      title: "Set 4: Problems with Size",
      context: `*Carlos en el probador*

Carlos se prueba unos pantalones pero hay un problema.
"Estos están muy ajustados. No puedo cerrar el botón," dice.
"Necesitas una talla mas grande" sugiere Ana.

*Carlos in the fitting room*

Carlos tries on some pants but there's a problem.
"These are too tight. I can't close the button," he says.
"You need a bigger size" suggests Ana.`,
      
      questions: [
        {
          type: "match",
          q: "Match the problem to the description:",
          pairs: [
            { left: "Too big", right: "Larger than needed" },
            { left: "Too small", right: "Smaller than needed" },
            { left: "Too expensive", right: "Costs too much" }
          ],
          a: "completed",
          successTip: "'TOO' = PROBLEM! Too big/small/expensive! ⚠️📏💸"
        },
        {
          type: "mc",
          q: "Carlos complains: 'This jacket is _____ small. I can't zip it!'",
          options: ["too", "very", "enough"],
          a: "too",
          tip: "Too + adjective = problem.",
          successTip: "Zip it = subir el cierre"
        },
        {
          type: "input",
          q: "These pants are too _____. I need a smaller size.",
          a: "big",
          alts: ["large", "loose"],
          successTip: "TOO BIG = need smaller! Size logic! 📏👖"
        },
        {
          type: "order",
          q: "Traduce: 'Este vestido es muy largo.'",
          words: ["This", "dress", "is", "too", "long."],
          a: "This dress is too long.",
          extra: ["short"],
          tip: "Too + adjective."
        },
        {
          type: "match",
          q: "Match the complaint to the solution:",
          pairs: [
            { left: "Too tight", right: "Get looser one" },
            { left: "Too short", right: "Get longer one" },
            { left: "Too old", right: "Get newer one" }
          ],
          a: "completed",
          successTip: "PROBLEMS → SOLUTIONS! Every problem has a fix! 🔧✅"
        },
        {
          type: "input",
          q: "Ana sees a coat she likes: 'I can't buy this coat. It's too _____!' $$$$",
          a: "expensive",
          tip: "Too expensive = can't afford."
        },
        {
          type: "mc",
          q: "Carlos tries on jeans: 'These jeans are too _____. They don't reach my ankles!'",
          options: ["short", "long", "wide"],
          a: "short",
          successTip: "DON'T REACH ANKLES = TOO SHORT! (No llega a los tobillos)! 👖🦶"
        },
        {
          type: "order",
          q: "Traduce: 'Los zapatos están muy ajustados.'",
          words: ["The", "shoes", "are", "too", "tight."],
          a: "The shoes are too tight.",
          extra: ["loose", "comfortable"],
          tip: "Too tight = uncomfortable."
        },
        {
          type: "input",
          q: "This shirt is too _____. I want a brighter color.",
          a: "dark",
          alts: ["black"],
          tip: "Too dark ≠ bright enough."
        },
        {
          type: "mc",
          q: "Ana complains: 'This skirt is too _____. I can't move!'",
          options: ["tight", "loose", "comfortable"],
          a: "tight",
          successTip: "CAN'T MOVE = TOO TIGHT! Uncomfortable! 😣"
        }
      ]
    },

    // ===================================================================
    // SET 5: NOT...ENOUGH (A2)
    // ===================================================================
    {
      id: "m3-u2-set5",
      title: "Set 5: Not Enough!",
      context: `*De vuelta en la tienda principal*

Carlos encuentra una chaqueta que le gusta, pero...
"Esta chamarra no es lo suficientemente caliente para el invierno," dice.
Ana está de acuerdo: "Tienes razón. Vamos a buscar una que sea caliente."

*Back in the main store*

Carlos finds a jacket he likes, but...
"This coat is not warm enough for winter," he says.
Ana agrees: "You're right. Let's look for a warm one."`,
      
      questions: [
        {
          type: "match",
          q: "Match the phrase to its equivalent:",
          pairs: [
            { left: "Too small", right: "Not big enough" },
            { left: "Too expensive", right: "Not cheap enough" },
            { left: "Too short", right: "Not long enough" }
          ],
          a: "completed",
          successTip: "SAME MEANING! 'Too small' = 'not big enough'! Different structure! 🔄"
        },
        {
          type: "mc",
          q: "Carlos says: 'This coat is not warm _____. I'm still cold!'",
          options: ["enough", "too", "very"],
          a: "enough",
          tip: "Not + adjective + enough."
        },
        {
          type: "input",
          q: "These shoes are not big _____. They hurt!",
          a: "enough",
          successTip: "NOT BIG ENOUGH = too small! Same problem! 👠😢"
        },
        {
          type: "order",
          q: "Traduce: 'El vestido no es suficientemente largo.'",
          words: ["The", "dress", "is", "not", "long", "enough."],
          a: "The dress is not long enough.",
          extra: ["short", "too long"],
          tip: "Not + adjective + enough."
        },
        {
          type: "match",
          q: "Match the statement to the meaning:",
          pairs: [
            { left: "Big enough", right: "Perfect size ✓" },
            { left: "Not big enough", right: "Too small ✗" },
            { left: "Too big", right: "Not small enough ✗" }
          ],
          a: "completed",
          successTip: "'ENOUGH' = sufficient, adequate! Perfect amount! ✅"
        },
        {
          type: "input",
          q: "This shirt is not cheap _____. I can't afford it.",
          a: "enough",
          tip: "Not cheap enough = too expensive."
        },
        {
          type: "mc",
          q: "Carlos complains: 'These pants are not _____ enough for my long legs!'",
          options: ["long", "short", "wide"],
          a: "long",
          successTip: "LONG LEGS need LONG PANTS! Logical! 👖📏"
        },
        {
          type: "order",
          q: "Traduce: 'Esta chamarra no es suficientemente cálida.'",
          words: ["This", "jacket", "is", "not", "warm", "enough."],
          a: "This jacket is not warm enough.",
          extra: ["cold", "dress"],
          tip: "Not + adjective + enough."
        },
        {
          type: "input",
          q: "The skirt is not _____ enough. It's too tight!",
          a: "wide",
          alts: ["loose", "big"],
          tip: "Not wide enough = too tight."
        },
        {
          type: "mc",
          q: "Sales assistant asks: 'Is this sweater warm enough?' Customer: '_____'",
          options: ["Yes, it's very warm", "No, it's warm", "It's enough warm"],
          a: "Yes, it's very warm",
          successTip: "ENOUGH goes AFTER adjective! 'Warm enough' NOT 'enough warm'! ✅"
        }
      ]
    },

    // ===================================================================
    // SET 6: SHOPPING DIALOGUES PART 1 (A2/B1-)
    // ===================================================================
    {
      id: "m3-u2-set6",
      title: "Set 6: Talking to the Clerk",
      context: `*En la sección de zapatos*

Carlos necesita ayuda. Se acerca a un empleado.
"Disculpe, puede ayudarme" pregunta Carlos.
"Por supuesto! Que esta buscando?" responde el empleado.

*In the shoe section*

Carlos needs help. He approaches a clerk.
"Excuse me, can you help me?" asks Carlos.
"Of course! What are you looking for?" answers the clerk.`,
      
      questions: [
        {
          type: "match",
          q: "Match the customer question to the clerk answer:",
          pairs: [
            { left: "Do you have this in medium?", right: "Let me check" },
            { left: "Can I try this on?", right: "Of course, fitting room is there" },
            { left: "How much is this?", right: "$50" }
          ],
          a: "completed",
          successTip: "REAL DIALOGUE! Customer asks, clerk helps! Shopping talk! 🛍️💬"
        },
        {
          type: "input",
          q: "Customer: 'Can I _____ this on?'",
          a: "try",
          alts: ["try on"],
          tip: "'Can I try this on?' = common question."
        },
        {
          type: "order",
          q: "Traduce: '¿Tienen esto en talla pequeña?'",
          words: ["Do", "you", "have", "this", "in", "small?", "size"],
          a: "Do you have this in small size?",
          extra: ["that", "get"],
          tip: "'Do you have this in [size]?'"
        },
        {
          type: "mc",
          q: "Clerk asks: 'Does it fit?' Customer responds: '_____'",
          options: ["Yes, it's perfect!", "Yes, I want", "Yes, it's expensive"],
          a: "Yes, perfect!",
          successTip: "PERFECT FIT = happy customer! ✅😊"
        },
        {
          type: "match",
          q: "Match the problem to the request:",
          pairs: [
            { left: "It's too small", right: "Can I try a larger size?" },
            { left: "It's too expensive", right: "Do you have anything cheaper?" },
            { left: "It's the wrong color", right: "Do you have this in blue?" }
          ],
          a: "completed",
          successTip: "PROBLEM → REQUEST! Polite way to ask for help! 🙋‍♂️"
        },
        {
          type: "input",
          q: "Completa: Cuanto cuesta este vestido?: 'How much is this ___?'",
          a: "dress",
          tip: "'How much?' = price question."
        },
        {
          type: "mc",
          q: "Completa: El probador esta por alla: 'The fitting room is _____.'",
          options: ["over there", "here this", "there here"],
          a: "over there",
          successTip: "'OVER THERE' = far from speaker! UK/US same! 👉"
        },
        {
          type: "order",
          q: "Traduce: '¿Puede ayudarme por favor?'",
          words: ["Can", "you", "help", "me", "please?"],
          a: "Can you help me please?",
          tip: "Polite request with 'please'."
        },
        {
          type: "input",
          q: "Clerk asks about payment: 'Would you like to _____ by card or cash?'",
          a: "pay",
          successTip: "'BY CARD OR CASH?' = payment options! 💳💵"
        },
        {
          type: "mc",
          q: "Customer decides to buy: 'I'll _____ this one, please.'",
          options: ["take", "try", "fit"],
          a: "take",
          successTip: "'I'LL TAKE THIS' = decision to buy! UK: 'have', US: 'take'! 🛍️"
        }
      ]
    },

    // ===================================================================
    // SET 7: ADVANCED SHOPPING SITUATIONS (A2+/B1-)
    // ===================================================================
    {
      id: "m3-u2-set7",
      title: "Set 7: Returns and Exchanges",
      context: `*Una semana después - De vuelta en la tienda*

Carlos regresa con los zapatos que compró.
"Necesito devolver estos. No me quedan bien" dice.
"Tiene el recibo?" pregunta el empleado.

*One week later - Back at the store*

Carlos returns with the shoes he bought.
"I need to return these. They don't fit well," he says.
"Do you have the receipt?" asks the clerk.`,
      
      questions: [
        {
          type: "match",
          q: "Match the problem to the complaint:",
          pairs: [
            { left: "Wrong size", right: "It doesn't fit" },
            { left: "Wrong color", right: "I want blue, not black" },
            { left: "Damaged", right: "There's a hole in it" }
          ],
          a: "completed",
          successTip: "RETURN REASONS! Wrong size/color, damaged! Valid complaints! 🔙"
        },
        {
          type: "order",
          q: "Traduce: 'Esta chaqueta no me queda bien.'",
          words: ["This", "jacket", "doesn't", "fit", "me."],
          a: "This jacket doesn't fit me.",
          extra: ["look", "suit"],
          tip: "'Doesn't fit me' = wrong size."
        },
        {
          type: "input",
          q: "Customer wants to exchange: 'Can I _____ this for a larger size?' (cambiar)",
          a: "exchange",
          alts: ["change", "swap"],
          tip: "Exchange = cambiar, swap.",
          successTip: " Swap es casual (entre amigos) y exchange es formal (en tiendas o negocios)."
        },
        {
          type: "mc",
          q: "Clerk asks for proof of purchase: 'Do you have the _____?'",
          options: ["receipt", "money", "card"],
          a: "receipt",
          successTip: "RECEIPT = proof of purchase! UK: 'till receipt', US: 'sales slip'! 🧾"
        },
        {
          type: "match",
          q: "Match the return reason to the action:",
          pairs: [
            { left: "Too small", right: "Exchange for larger" },
            { left: "Don't like color", right: "Exchange for different color" },
            { left: "Changed mind", right: "Get refund" }
          ],
          a: "completed",
          successTip: "RETURN OPTIONS! Exchange or refund! (cambio o reembolso)Store policy! 🔄💰"
        },
        {
          type: "input",
          q: "Clerk apologizes: 'I'm sorry, we don't _____ this in stock anymore.'",
          a: "have",
          tip: "'In stock' = available in store.",
          successTip: "'In stock' = available! 'Out of stock' = sold out! 🏪❌"
        },
        {
          type: "mc",
          q: "Customer asks about discount: 'Is this on _____?' Clerk: 'Yes, 30% off!'",
          options: ["sale", "sell", "sold"],
          a: "sale",
          successTip: "'ON SALE' = discounted! UK: 'in the sale', US: 'on sale'! 🏷️"
        },
        {
          type: "order",
          q: "Traduce: '¿Tienen algo más barato?'",
          words: ["Do", "you", "have", "anything", "cheaper?"],
          a: "Do you have anything cheaper?",
          extra: ["more", "less"],
          tip: "'Cheaper?' = mas barato.",
          successTip: "Anything cheaper = something at a lower price! 💸"
        },
        {
          type: "input",
          q: "This shirt was $50, but now it's $35. It's on _____.",
          a: "sale",
          alts: ["discount"],
          tip: "On sale = discounted price."
        },
        {
          type: "mc",
          q: "Customer wants money back: 'Can I get a _____? I don't want an exchange.'",
          options: ["refund", "receipt", "discount"],
          a: "refund",
          successTip: "REFUND = money back! UK: 'refund', US: 'money back'! 💵"
        }
      ]
    },

    // ===================================================================
    // SET 8: MIXED PRACTICE (A2+/B1-)
    // ===================================================================
    {
      id: "m3-u2-set8",
      title: "Set 8: Shopping Skills Review",
      context: `*Última tienda del día*

Ana y Carlos revisan lo que aprendieron hoy.
"Nos probamos mucha ropa" dice Ana. "y aprendimos a pedir ayuda" añade Carlos.
Es hora de ir a casa.

*Last store of the day*

Ana and Carlos review what they learned today.
"We tried on many clothes," says Ana. "And we learned to ask for help," adds Carlos.
Time to go home.`,
      
      questions: [
        {
          type: "match",
          q: "Match the verb to the shopping stage:",
          pairs: [
            { left: "Try on", right: "Before buying" },
            { left: "Pay", right: "At checkout" },
            { left: "Exchange", right: "After buying (a different one)"},
          { left: "Refund", right: "After buying (money back)" }
          ],
          a: "completed",
          successTip: "SHOPPING SEQUENCE! Try→buy→pay→(maybe exchange)! Complete cycle! 🛒🔄"
        },
        {
          type: "input",
          q: "This shop _____ beautiful leather bags.",
          a: "sells",
          alts: ["sell","has","offers","have"],
          tip: "Shop (singular) → sells."
        },
        {
          type: "mc",
          q: "These pants are _____ big. I need a smaller size.",
          options: ["too", "enough", "very"],
          a: "too",
          successTip: "TOO = problem! Need smaller! ⚠️"
        },
        {
          type: "order",
          q: "Traduce: 'Siempre me pruebo la ropa antes de comprar.'",
          words: ["I", "always", "try", "on", "clothes", "before", "buying."],
          a: "I always try on clothes before buying.",
          tip: "Adverb (always) goes before main verb."
        },
        {
          type: "input",
          q: "Customer asks: 'Does this fit?' Clerk: 'Try it ___ and see.'",
          a: "on",
          tip: "'Try on' = put on to test.",
          successTip: "'Try and see' = test to find out."
        },
        {
          type: "mc",
          q: "This coat is not warm _____. I'm cold!",
          options: ["enough", "too", "very"],
          a: "enough",
          tip: "Not + adjective + enough."
        },
        {
          type: "order",
          q: "Traduce: '¿Cuánto cuesta esta chaqueta?'",
          words: ["How", "much", "is", "this", "jacket?"],
          a: "How much is this jacket?",
          extra: ["cost", "money", "price"],
          tip: "'How much?' = price question.",
          successTip: "'How much does it cost?' is also correct!"
        },
        {
          type: "input",
          q: "I _____ by credit card because it's convenient.",
          a: "pay",
          successTip: "PAY BY CARD! UK: 'contactless', US: 'tap & pay'! Convenient! 💳✨"
        },
        {
          type: "mc",
          q: "Ana _____ 20 pairs of shoes! She loves shopping!",
          options: ["owns", "own", "has", "have"],
          a: "owns",
          tip: "She → owns (third person)."
        },
        {
          type: "order",
          q: "Traduce: '¡Me encanta comprar ropa nueva!'",
          words: ["I", "love", "shopping", "for", "new", "clothes!"],
          a: "I love shopping for new clothes!",
          successTip: "SHOPPING COMPLETE! You mastered shopping vocabulary! Next: scenarios! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 9: CONTEXT-BASED SCENARIOS (B1-)
    // ===================================================================
    {
      id: "m3-u2-set9",
      title: "Set 9: Real Shopping Scenarios",
      context: `*Centro comercial - Diferentes situaciones*

Durante el día, Ana y Carlos ven muchas situaciones de compras:
- Una cliente devuelve zapatos
- Un cliente busca un regalo
- Una familia compra ropa para una boda

*Shopping mall - Different situations*

During the day, Ana and Carlos see many shopping situations:
- A customer returns shoes
- A customer looks for a gift
- A family buys clothes for a wedding`,
      
      questions: [
        {
          type: "match",
          q: "Match the situation to the best action:",
          pairs: [
            { left: "Jacket too tight", right: "Try larger size" },
            { left: "Love the dress, but expensive", right: "Wait for sale" },
            { left: "Shoes don't fit", right: "Exchange them" }
          ],
          a: "completed",
          successTip: "SMART SHOPPING! Practical solutions for real problems! 🤔✅"
        },
        {
          type: "order",
          q: "Clerk's opening: Traduce: '¿Puedo ayudarle?'",
          words: ["Can", "I", "help", "you?"],
          a: "Can I help you?",
          extra: ["jeans","he"],
          tip: "Standard clerk greeting."
        },
        {
          type: "input",
          q: "Customer responds: 'Yes, I _____ for a birthday gift.'",
          a: "look",
          alts: ["searching", "looking", "am looking","'m looking"],
          tip: "'Looking for' = searching for."
        },
        {
          type: "mc",
          q: "Clerk asks for more details: 'What _____ are you looking for?'",
          options: ["large", "jacket", "size and color"],
          a: "size and color",
          successTip: "CLERKS ASK DETAILS! Size AND color! Helpful! 🎨📏"
        },
        {
          type: "input",
          q: "Customer specifies: 'Medium, please. Do you have it in _____?' (negro)",
          a: "black",
          tip: "Common color request."
        },
        {
          type: "order",
          q: "Clerk goes to check: Traduce: 'Déjeme revisar en la bodega.'",
          words: ["Let", "me", "check", "in", "the", "back."],
          a: "Let me check in the back.",
          tip: "'In the back' = storage area."
        },
        {
          type: "match",
          q: "Match the shopper type to their behavior:",
          pairs: [
            { left: "Careful shopper", right: "Tries everything on" },
            { left: "Impulse buyer", right: "Buys without trying" },
            { left: "Bargain hunter", right: "Waits for sales" }
          ],
          a: "completed",
          successTip: "Bargain hunters = Cazadores de ofertas! 🛍️😎💰"
        },
        {
          type: "input",
          q: "Clerk announces promotion: 'We have a _____ today! 20% off everything!'",
          a: "sale",
          alts: ["discount", "promotion"],
          tip: "Sale = discounted prices."
        },
        {
          type: "mc",
          q: "Customer decides: 'Great! I'll _____ three shirts then!'",
          options: ["take", "try", "pay"],
          a: "take",
          successTip: "'I'LL TAKE' = decision to purchase! UK/US same! 🛍️"
        },
        {
          type: "order",
          q: "Clerk announces total: Traduce: 'Son $60 por favor.'",
          words: ["That", "will", "be", "$60", "please."],
          a: "That will be $60 please.",
          successTip: "TRANSACTION COMPLETE! 'That will be...' = formal total! 💵"
        }
      ]
    },

    // ===================================================================
    // SET 10: ADVANCED VOCABULARY & EXPRESSIONS (B1-)
    // ===================================================================
    {
      id: "m3-u2-set10",
      title: "Set 10: Shopping Like a Pro",
      context: `*Última hora en el centro comercial*

Ana y Carlos están cansados pero felices.
"Aprendimos mucho hoy!" dice Ana. "Si, ahora podemos comprar confiadamente en inglés," responde Carlos.
Van a la salida con sus bolsas de compras.

*Final hour at the mall*

Ana and Carlos are tired but happy.
"We learned so much today!" says Ana. "Yes! Now we can shop confidently in English," responds Carlos.
They head to the exit with their shopping bags.`,
      
      questions: [
        {
          type: "match",
          q: "Match the advanced term to its meaning:",
          pairs: [
            { left: "Fitting room", right: "Where you try clothes" },
            { left: "Checkout", right: "Where you pay" },
            { left: "Receipt", right: "Proof of purchase" },
            { left: "Sign up", right: "Subscribe for something" }
          ],
          a: "completed",
          successTip: "PRO VOCABULARY! UK: 'changing room', US: 'fitting room'! 🏬"
        },
        {
          type: "input",
          q: "The _____ room is at the back of the store.",
          a: "fitting",
          alts: ["changing", "dressing"],
          tip: "UK: changing/fitting, US: fitting/dressing room."
        },
        {
          type: "mc",
          q: "Customer asks about payment: 'Do you accept _____?'",
          options: ["credit cards", "expensive", "fitting"],
          a: "credit cards",
          successTip: "'DO YOU ACCEPT...?' = payment method question! 💳"
        },
        {
          type: "order",
          q: "Customer asks about policy: Traduce: '¿Tienen política de devolución?'",
          words: ["Do", "you", "have", "a", "return", "policy?"],
          a: "Do you have a return policy?",
          tip: "Return policy = rules for returning items."
        },
        {
          type: "match",
          q: "Match the store section to what it sells:",
          pairs: [
            { left: "Menswear", right: "Men's clothing" },
            { left: "Womenswear", right: "Women's clothing" },
            { left: "Accessories", right: "Belts, bags, scarves" }
          ],
          a: "completed",
          successTip: "STORE LAYOUT! UK: 'department store', US: 'department store' too! 🏬"
        },
        {
          type: "input",
          q: "This store has a good _____ policy. You can return within 30 days.",
          a: "return",
          alts: ["refund"],
          tip: "Return policy = rules for returns."
        },
        {
          type: "mc",
          q: "Clerk offers membership: 'Would you like to _____  for our loyalty program?'",
          options: ["sign up", "write up", "make up"],
          a: "sign up",
          successTip: "'SIGN UP' = register! UK: 'sign up', US: 'join'! 📝"
        },
        {
          type: "order",
          q: "Clerk gives advice: Traduce: 'Guarde su recibo para devoluciones.'",
          words: ["Keep", "your", "receipt", "for", "returns."],
          a: "Keep your receipt for returns.",
          extra: ["buying", "exchanges"],
          tip: "Keep receipt = save for later."
        },
        {
          type: "input",
          q: "Customer asks: 'Is there a _____ on this item?' Clerk: 'Yes, 15% off!'",
          a: "discount",
          alts: ["sale", "reduction"],
          successTip: "Para decir que algo tiene % de descuento usa off. '15% off!' 🏷️"
        },
        {
          type: "mc",
          q: "Final question: What's the MOST important shopping tip?",
          options: ["Always try on clothes!", "Buy the most expensive", "Never ask for help"],
          a: "Always try on clothes!",
          successTip: "🏆 UNIT 2 COMPLETE! Golden rule: ALWAYS try on! Good fit = happy you! Now: Reading! 📖"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Sarah's Shopping Day (10 PREGUNTAS)
  // ===================================================================
  readingSet: {
    id: "m3-u2-reading",
    title: "Reading: Sarah's Big Day",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**A Special Occasion**

Sarah goes to the mall on Saturday morning. She needs new clothes for her job interview next week.

First, she goes to a clothing store called "Fashion First." She sees a beautiful blue dress. "Can I try this on?" she asks the clerk. "Of course! The fitting room is over there," says the clerk.`
      },
      {
        type: "mc",
        q: "When does Sarah go shopping?",
        options: ["Saturday morning", "Sunday afternoon", "Friday evening"],
        a: "Saturday morning",
        tip: "Check the first sentence.",
        successTip: "✓ MORNING SHOPPING! Best time = less crowded! ☀️🛍️"
      },
      {
        type: "input",
        q: "Why does Sarah need new clothes? -For her job _____",
        a: "interview",
        alts: ["job interview", "work interview"],
        tip: "The passage says 'for her job interview'.",
        successTip: "✓ INTERVIEW! Professional attire = important first impression! 💼"
      },
      {
        type: "mc",
        q: "What is the name of the clothing store?",
        options: ["Fashion First", "Style Shop", "Dress Store"],
        a: "Fashion First",
        tip: "Look for the name in quotation marks.",
        successTip: "✓ FASHION FIRST! Store names are always capitalized! 👗"
      },

      {
        type: "passage",
        passage: `**The Perfect Fit**

Sarah tries on the dress, but it doesn't fit. It's too small. "Do you have this in a larger size?" she asks. The clerk brings size medium. 

"Perfect!" says Sarah. "It fits well now. How much is it?" 
"It's $80, but we have a sale today. It's 20% off, so it's $64," says the clerk.`
      },
      {
        type: "mc",
        q: "Why doesn't the first dress fit?",
        options: ["It's too small", "It's too big", "It's too long"],
        a: "It's too small",
        tip: "Read Sarah's question to the clerk.",
        successTip: "✓ TOO SMALL! That's why she needs a larger size! 📏"
      },
      {
        type: "input",
        q: "What size does Sarah buy?",
        a: "medium",
        alts: ["Medium", "M", "m"],
        successTip: "✓ SIZE M! Perfect fit! Always try before buying! ✅"
      },
      {
        type: "mc",
        q: "How much does Sarah pay after the discount?",
        options: ["$64", "$80", "$45"],
        a: "$64",
        tip: "$80 minus 20% = ?",
        successTip: "✓ SALE PRICE! $80 - 20% = $64! Good deal! 💰"
      },

      {
        type: "passage",
        passage: `**Shoes and Payment**

After buying the dress, Sarah goes to a shoe store. She needs black shoes for work. She tries on three pairs. The first pair is too tight. The second pair is too expensive. The third pair is perfect! They fit well and cost only $45.

At the checkout, the clerk asks: "How would you like to pay?" 
"By credit card, please," says Sarah. "Would you like your receipt?" 
"Yes, please. Just in case I need to return them."`
      },
      {
        type: "input",
        q: "How many pairs of shoes does Sarah try on?",
        a: "three",
        alts: ["3", "Three"],
        successTip: "✓ THREE! Persistence pays off! Third time's the charm! 👠✨"
      },
      {
        type: "mc",
        q: "How does Sarah pay for her purchases?",
        options: ["By credit card", "With cash", "By debit card"],
        a: "By credit card",
        tip: "Look at Sarah's answer to the clerk.",
        successTip: "✓ CREDIT CARD! Fast and secure payment! 💳"
      },
      {
        type: "input",
        q: "Why does Sarah keep the receipt? -In case she needs to _____ them",
        a: "return",
        alts: ["exchange"],
        tip: "Check the last sentence: 'Just in case I need to...'",
        successTip: "✓ SMART SHOPPER! Always keep receipts for returns! 🧾✅"
      },
      {
        type: "mc",
        q: "What color shoes does Sarah buy?",
        options: ["Black", "Blue", "Brown"],
        a: "Black",
        tip: "The passage says 'She needs black shoes for work'.",
        successTip: "✓ BLACK SHOES! Professional color for interviews! Ready to succeed! 🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m3-u2-review",
    title: "Unit 2 Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Review: Match shopping verbs to meanings:",
        pairs: [
          { left: "Buy", right: "Pay for" },
          { left: "Sell", right: "Give for money" },
          { left: "Try on", right: "Test clothing" }
        ],
        a: "completed",
        successTip: "✓ BASICS REMEMBERED! Shopping verbs mastered! 🛍️"
      },
      {
        type: "input",
        q: "Review: This jacket is too _____. I need a bigger one. (pequeño)",
        a: "small",
        alts: ["tight"],
        tip: "Too + adjective = problem."
      },
      {
        type: "mc",
        q: "Review: These shoes are not big _____.",
        options: ["enough", "too", "very"],
        a: "enough",
        successTip: "✓ NOT...ENOUGH! Not big enough = too small! 📏"
      },
      {
        type: "order",
        q: "Review: Traduce: '¿Te queda bien este vestido?'",
        words: ["Does", "this", "dress", "fit", "you?"],
        a: "Does this dress fit you?",
        tip: "Common fitting room question."
      },
      {
        type: "input",
        q: "Review: I always _____ _____ clothes before buying. (probar)",
        a: "try on",
        alts: ["try"],
        tip: "Try on = test before buying."
      },
      {
        type: "mc",
        q: "Review: She _____ by credit card.",
        options: ["pays", "pay", "paid"],
        a: "pays",
        tip: "She → pays (third person)."
      },
      {
        type: "order",
        q: "Review: Traduce: '¿Puedo probarme esto?'",
        words: ["Can", "I", "try", "this", "on?"],
        a: "Can I try this on?",
        tip: "Common customer question."
      },
      {
        type: "input",
        q: "Review: This shop _____ beautiful clothes. (vender)",
        a: "sells",
        alts: ["sell"],
        successTip: "✓ SHOP → SELLS! Third person rule! 🏪"
      },
      {
        type: "mc",
        q: "Review: Customer asks: 'How _____ is this?' Clerk: '$50'",
        options: ["much", "many", "old"],
        a: "much",
        tip: "'How much?' = price question."
      },
      {
        type: "order",
        q: "Review: Traduce: '¡Me encanta comprar ropa!'",
        words: ["I", "love", "shopping", "for", "clothes!"],
        a: "I love shopping for clothes!",
        successTip: "🏆 UNIT 2 COMPLETE! You can now shop confidently in English! Next: Unit 3! 🎉"
      }
    ]
  }
};
